import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Clock, Save, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = [
  { key: 'monday', label: 'Lunes' },
  { key: 'tuesday', label: 'Martes' },
  { key: 'wednesday', label: 'Miércoles' },
  { key: 'thursday', label: 'Jueves' },
  { key: 'friday', label: 'Viernes' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' },
];

const DEFAULT_DAY = { enabled: false, shifts: [{ open: '09:00', close: '14:00' }] };

const defaultHours = () => ({
  monday: { enabled: true, shifts: [{ open: '09:00', close: '14:00' }, { open: '16:00', close: '19:00' }] },
  tuesday: { enabled: true, shifts: [{ open: '09:00', close: '14:00' }, { open: '16:00', close: '19:00' }] },
  wednesday: { enabled: true, shifts: [{ open: '09:00', close: '14:00' }, { open: '16:00', close: '19:00' }] },
  thursday: { enabled: true, shifts: [{ open: '09:00', close: '14:00' }, { open: '16:00', close: '19:00' }] },
  friday: { enabled: true, shifts: [{ open: '09:00', close: '14:00' }, { open: '16:00', close: '19:00' }] },
  saturday: { enabled: false, shifts: [{ open: '09:00', close: '14:00' }] },
  sunday: { enabled: false, shifts: [{ open: '', close: '' }] },
  timezone: 'Europe/Madrid',
});

const AdminBusinessHoursPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hours, setHours] = useState(defaultHours());

  useEffect(() => {
    axios.get(`${API}/business-hours`)
      .then(r => {
        const h = r.data.hours;
        if (h && h.monday) {
          setHours(h);
        } else if (h && h.weekdays) {
          // Migrate old format
          const migrated = defaultHours();
          ['monday','tuesday','wednesday','thursday','friday'].forEach(d => {
            migrated[d] = { enabled: !!h.weekdays.open, shifts: [{ open: h.weekdays.open || '09:00', close: h.weekdays.close || '18:00' }] };
          });
          migrated.saturday = { enabled: !!h.saturday?.open, shifts: [{ open: h.saturday?.open || '', close: h.saturday?.close || '' }] };
          migrated.sunday = { enabled: !!h.sunday?.open, shifts: [{ open: h.sunday?.open || '', close: h.sunday?.close || '' }] };
          setHours(migrated);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/business-hours`, { hours }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Horario actualizado correctamente');
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const toggleDay = (day) => {
    setHours(prev => ({ ...prev, [day]: { ...prev[day], enabled: !prev[day]?.enabled } }));
  };

  const updateShift = (day, idx, field, value) => {
    setHours(prev => {
      const shifts = [...(prev[day]?.shifts || [])];
      shifts[idx] = { ...shifts[idx], [field]: value };
      return { ...prev, [day]: { ...prev[day], shifts } };
    });
  };

  const addShift = (day) => {
    setHours(prev => {
      const shifts = [...(prev[day]?.shifts || [])];
      shifts.push({ open: '16:00', close: '19:00' });
      return { ...prev, [day]: { ...prev[day], shifts } };
    });
  };

  const removeShift = (day, idx) => {
    setHours(prev => {
      const shifts = (prev[day]?.shifts || []).filter((_, i) => i !== idx);
      return { ...prev, [day]: { ...prev[day], shifts: shifts.length ? shifts : [{ open: '', close: '' }] } };
    });
  };

  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="business-hours-page">
        <div className="flex items-center gap-3">
          <Clock className="w-8 h-8 text-primary" />
          <div>
            <h1 className="font-heading text-2xl font-bold text-white">HORARIO COMERCIAL</h1>
            <p className="text-zinc-400 text-sm">Configura el horario de atención por día. Soporta jornada partida.</p>
          </div>
        </div>

        <div className="bg-surface border border-white/10 rounded-md p-6 space-y-1">
          <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-4 block">Zona horaria: Europe/Madrid (CET/CEST)</Label>

          {DAYS.map(({ key, label }) => {
            const day = hours[key] || DEFAULT_DAY;
            return (
              <div key={key} className={`flex items-start gap-3 py-3 border-b border-white/5 ${!day.enabled ? 'opacity-50' : ''}`}>
                <button
                  type="button"
                  onClick={() => toggleDay(key)}
                  className={`mt-1 w-10 h-5 rounded-full relative transition-colors flex-shrink-0 ${day.enabled ? 'bg-primary' : 'bg-zinc-700'}`}
                  data-testid={`toggle-${key}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${day.enabled ? 'left-5' : 'left-0.5'}`} />
                </button>
                <span className="w-24 text-zinc-300 text-sm font-medium mt-1 flex-shrink-0">{label}</span>
                <div className="flex-1 space-y-2">
                  {day.enabled ? (
                    <>
                      {(day.shifts || []).map((shift, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Input type="time" value={shift.open || ''} onChange={(e) => updateShift(key, idx, 'open', e.target.value)}
                            className="bg-surface-highlight border-white/10 text-white w-28" data-testid={`${key}-shift${idx}-open`} />
                          <span className="text-zinc-500 text-xs">a</span>
                          <Input type="time" value={shift.close || ''} onChange={(e) => updateShift(key, idx, 'close', e.target.value)}
                            className="bg-surface-highlight border-white/10 text-white w-28" data-testid={`${key}-shift${idx}-close`} />
                          {idx > 0 && (
                            <button type="button" onClick={() => removeShift(key, idx)} className="text-red-500 hover:text-red-400 p-1">
                              <X className="w-4 h-4" />
                            </button>
                          )}
                          {idx === 0 && (day.shifts || []).length < 2 && (
                            <button type="button" onClick={() => addShift(key)}
                              className="text-primary hover:text-primary/80 text-xs flex items-center gap-1 ml-2" data-testid={`${key}-add-shift`}>
                              <Plus className="w-3.5 h-3.5" /> Turno tarde
                            </button>
                          )}
                        </div>
                      ))}
                    </>
                  ) : (
                    <span className="text-zinc-600 text-sm mt-1 block">Cerrado</span>
                  )}
                </div>
              </div>
            );
          })}

          <div className="pt-4">
            <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 text-white" data-testid="save-hours-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar Horario
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminBusinessHoursPage;
